/* ==========================================================================
   Rook UI Core — Reveal Controller
   Web Component: <rk-reveal-element>

   Progressive scroll reveal via IntersectionObserver. Animations:
   fade-up, fade-down, fade-left, fade-right, fade, zoom. Supports stagger
   between direct children of the content.

   Hydration tier: 'eager' (deviation from plan — 'visible' would cause a
   visual flash because initial-hidden state must apply before viewport entry).
   The internal IntersectionObserver handles the lazy reveal trigger.
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkRevealElement extends RkComponent {
    static hydration = 'eager';

    #observer = null;
    #hasRevealed = false;

    setup() {
        this._animation = this.dataset.animation || 'fade-up';
        this._duration = parseInt(this.dataset.duration) || 600;
        this._delay = parseInt(this.dataset.delay) || 0;
        this._stagger = parseInt(this.dataset.stagger) || 0;
        this._threshold = parseFloat(this.dataset.threshold) || 0.1;
        this._easing = this.dataset.easing || 'cubic-bezier(0.16, 1, 0.3, 1)';
        this._once = this.dataset.once !== 'false';

        this.#applyInitialState();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.#revealImmediate();
            return;
        }

        this.#observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.#reveal();
                        if (this._once) {
                            this.#observer.unobserve(this);
                        }
                    } else if (!this._once && this.#hasRevealed) {
                        this.#hide();
                    }
                });
            },
            { threshold: this._threshold }
        );

        this.#observer.observe(this);
    }

    teardown() {
        this.#observer?.disconnect();
        this.#observer = null;
    }

    #applyInitialState() {
        const content = this.querySelector('.rk-reveal__content');
        if (!content) return;

        content.style.transition = 'none';
        content.style.willChange = 'opacity, transform';

        const transforms = {
            'fade-up': 'translateY(30px)',
            'fade-down': 'translateY(-30px)',
            'fade-left': 'translateX(30px)',
            'fade-right': 'translateX(-30px)',
            'fade': 'none',
            'zoom': 'scale(0.92)',
        };

        content.style.opacity = '0';
        const transform = transforms[this._animation] || transforms['fade-up'];
        if (transform !== 'none') {
            content.style.transform = transform;
        }

        if (this._stagger > 0) {
            const children = content.children;
            for (let i = 0; i < children.length; i++) {
                children[i].style.opacity = '0';
                children[i].style.willChange = 'opacity, transform';
                if (transform !== 'none') {
                    children[i].style.transform = transform;
                }
            }
        }
    }

    #reveal() {
        this.#hasRevealed = true;
        const content = this.querySelector('.rk-reveal__content');
        if (!content) return;

        if (this._stagger > 0) {
            this.#revealWithStagger(content);
        } else {
            this.#revealSingle(content);
        }

        this.classList.add('rk-reveal--visible');
        this.rkEmit('reveal:visible', { animation: this._animation });
    }

    #revealSingle(content) {
        requestAnimationFrame(() => {
            content.style.transition = `opacity ${this._duration}ms ${this._easing} ${this._delay}ms, transform ${this._duration}ms ${this._easing} ${this._delay}ms`;
            content.style.opacity = '1';
            content.style.transform = 'translate(0) scale(1)';
        });
    }

    #revealWithStagger(content) {
        content.style.transition = `opacity ${this._duration}ms ${this._easing} ${this._delay}ms`;
        content.style.opacity = '1';
        content.style.transform = 'none';

        const children = content.children;
        for (let i = 0; i < children.length; i++) {
            const childDelay = this._delay + (this._stagger * i);
            requestAnimationFrame(() => {
                children[i].style.transition = `opacity ${this._duration}ms ${this._easing} ${childDelay}ms, transform ${this._duration}ms ${this._easing} ${childDelay}ms`;
                children[i].style.opacity = '1';
                children[i].style.transform = 'translate(0) scale(1)';
            });
        }
    }

    #hide() {
        this.#hasRevealed = false;
        const content = this.querySelector('.rk-reveal__content');
        if (!content) return;

        this.classList.remove('rk-reveal--visible');
        this.#applyInitialState();

        requestAnimationFrame(() => {
            content.style.transition = `opacity ${this._duration}ms ${this._easing}, transform ${this._duration}ms ${this._easing}`;
            if (this._stagger > 0) {
                const children = content.children;
                for (let i = 0; i < children.length; i++) {
                    children[i].style.transition = `opacity ${this._duration}ms ${this._easing}, transform ${this._duration}ms ${this._easing}`;
                }
            }
        });
    }

    #revealImmediate() {
        const content = this.querySelector('.rk-reveal__content');
        if (!content) return;

        content.style.opacity = '1';
        content.style.transform = 'none';
        content.style.transition = 'none';

        if (this._stagger > 0) {
            const children = content.children;
            for (let i = 0; i < children.length; i++) {
                children[i].style.opacity = '1';
                children[i].style.transform = 'none';
                children[i].style.transition = 'none';
            }
        }

        this.classList.add('rk-reveal--visible');
    }
}

if (!customElements.get('rk-reveal-element')) {
    customElements.define('rk-reveal-element', RkRevealElement);
}
